<template>
  <vs-popup
    class="apple-modal modal-regra-cobranca"
    :title="isEdit ? 'Editar Regra de Cobrança' : 'Nova Regra de Cobrança'"
    :active.sync="activeLocal"
    @close="fechar"
  >
    <div class="apple-modal-body">
      <!-- Callout Apple -->
      <div class="apple-callout mb-4">
        <feather-icon icon="InfoIcon" svgClasses="w-4 h-4 text-blue-600 mr-2.5 flex-shrink-0" />
        <p class="apple-callout-text">
          Configure lembretes preventivos ou cobranças de faturas em atraso conforme a <strong>data de vencimento</strong> e as <strong>formas de pagamento</strong> para envio via WATI.
        </p>
      </div>

      <div class="apple-form-grid">
        <!-- Nome da Regra -->
        <div class="apple-form-group col-span-12">
          <label class="apple-label">Nome / Identificação da Regra <span class="text-rose-500">*</span></label>
          <input
            v-model="form.nome"
            type="text"
            class="apple-native-input"
            placeholder="Ex: Lembrete Boleto e Depósito - 2 dias antes do vencimento"
          />
        </div>

        <!-- Momento do Disparo -->
        <div class="apple-form-group col-span-6">
          <label class="apple-label">Momento do Disparo <span class="text-rose-500">*</span></label>
          <el-select
            v-model="form.tipo_periodo"
            class="w-full apple-el-select"
            size="small"
            popper-class="regua-el-popper"
            placeholder="Selecione o momento"
          >
            <el-option value="antes" label="Antes do Vencimento (A Vencer)" />
            <el-option value="no_vencimento" label="No Dia do Vencimento" />
            <el-option value="apos" label="Após o Vencimento (Em Atraso)" />
          </el-select>
          <span class="apple-helper-text">Relação com o vencimento</span>
        </div>

        <!-- Quantidade de Dias -->
        <div class="apple-form-group col-span-6">
          <label class="apple-label">Quantidade de Dias <span class="text-rose-500">*</span></label>
          <input
            v-model="form.dias_relativo_vencimento"
            type="number"
            min="0"
            class="apple-native-input"
            :disabled="form.tipo_periodo === 'no_vencimento'"
            :placeholder="form.tipo_periodo === 'no_vencimento' ? '0' : 'Ex: 2, 7, 14, 30, 60'"
          />
          <span class="apple-helper-text">
            {{ form.tipo_periodo === 'antes' ? 'Dias de antecedência' : (form.tipo_periodo === 'apos' ? 'Dias de atraso' : 'Disparo na data exata de vencimento') }}
          </span>
        </div>

        <!-- Formas de Pagamento (Multi-Seleção com Badges) -->
        <div class="apple-form-group col-span-12">
          <div class="apple-form-header-row">
            <label class="apple-label m-0">Formas de Pagamento Permitidas</label>
            <div class="apple-form-header-actions">
              <button
                type="button"
                class="apple-link-action"
                @click="selecionarTodasFormas"
              >
                Selecionar Todas
              </button>
              <span class="action-separator">·</span>
              <button
                type="button"
                class="apple-link-action action-danger"
                @click="limparFormas"
              >
                Limpar Seleção (Todas as Formas)
              </button>
            </div>
          </div>

          <el-select
            v-model="form.ids_formas_pagamento"
            class="w-full apple-el-select"
            size="small"
            multiple
            filterable
            clearable
            collapse-tags
            popper-class="regua-el-popper"
            placeholder="Todas as Formas de Pagamento (Sem restrição)"
          >
            <el-option
              v-for="forma in listaFormasPagamento"
              :key="forma.id"
              :value="forma.id"
              :label="forma.nome"
            />
          </el-select>

          <!-- Badges Elegantes e Interativos das Formas Selecionadas -->
          <div class="mt-2.5 flex items-center flex-wrap" style="gap: 8px;">
            <div v-if="!form.ids_formas_pagamento || form.ids_formas_pagamento.length === 0">
              <span class="apple-pill-badge badge-neutral" style="font-size: 11.5px; padding: 4px 10px;">
                💳 Todas as Formas de Pagamento (Sem restrição)
              </span>
            </div>
            <div
              v-for="formaId in form.ids_formas_pagamento"
              :key="formaId"
              class="forma-chip-badge"
              :class="obterBadgeClass(formaId)"
            >
              <AppIcon :name="obterFormaIcon(formaId)" size="12" class="mr-1 inline-flex shrink-0" />
              <span class="forma-chip-name">{{ obterFormaNome(formaId) }}</span>
              <button
                type="button"
                class="forma-chip-remove"
                title="Remover esta forma de pagamento"
                @click.stop="removerForma(formaId)"
              >
                ×
              </button>
            </div>
          </div>
          <span class="apple-helper-text mt-1.5 block">
            Selecione uma ou mais formas específicas (ex: Boleto + Depósito) ou deixe vazio para incluir todas as formas.
          </span>
        </div>

        <!-- Template WATI -->
        <div class="apple-form-group col-span-12">
          <div class="apple-form-header-row">
            <label class="apple-label m-0">Template do WATI <span class="text-rose-500">*</span></label>
            <div class="apple-form-header-actions">
              <button
                type="button"
                class="apple-link-action"
                :disabled="loadingTemplates"
                @click="$emit('sincronizar-templates')"
                title="Sincronizar templates do WATI agora"
              >
                <feather-icon icon="RefreshCwIcon" svgClasses="w-3 h-3 mr-1 inline" :class="{ 'animate-spin': loadingTemplates }" />
                <span>Atualizar Lista</span>
              </button>
              <span class="action-separator">·</span>
              <router-link to="/parametro" target="_blank" class="apple-link-action">
                Configurações WATI
              </router-link>
            </div>
          </div>
          <el-select
            v-model="form.template_nome"
            class="w-full apple-el-select"
            size="small"
            filterable
            popper-class="regua-el-popper"
            placeholder="Selecione o template WATI aprovado"
            :loading="loadingTemplates"
          >
            <el-option
              v-for="tpl in templates"
              :key="tpl.templateName || tpl.elementName || tpl.name"
              :value="tpl.templateName || tpl.elementName || tpl.name"
              :label="(tpl.templateName || tpl.elementName || tpl.name) + (tpl.category ? ' (' + tpl.category + ')' : '')"
            />
          </el-select>
        </div>

        <!-- Equipes Opcional -->
        <div class="apple-form-group col-span-12">
          <label class="apple-label">Direcionar para Equipe WATI <span class="text-gray-400 font-normal">(Opcional)</span></label>
          <input
            v-model="form.equipes"
            type="text"
            class="apple-native-input"
            placeholder="Ex: Financeiro, Cobranca (separadas por vírgula)"
          />
        </div>

        <!-- Switch Ativo / Inativo Apple -->
        <div class="col-span-12 apple-switch-card">
          <div>
            <div class="text-sm font-semibold text-gray-900">Status da Regra</div>
            <div class="text-xs text-gray-500 mt-0.5">Regras ativas disparam mensagens automaticamente</div>
          </div>
          <vs-switch v-model="form.ativo" color="success" />
        </div>
      </div>

      <!-- Rodapé Apple -->
      <div class="apple-dialog-footer">
        <button type="button" class="apple-dialog-btn apple-btn-cancel" @click="fechar">
          Cancelar
        </button>
        <button type="button" class="apple-dialog-btn apple-btn-save" :disabled="salvando" @click="salvar">
          {{ salvando ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Criar Regra') }}
        </button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "ModalRegraCobranca",
  props: {
    active: {
      type: Boolean,
      default: false
    },
    regra: {
      type: Object,
      default: null
    },
    templates: {
      type: Array,
      default: function () {
        return [];
      }
    },
    formasPagamento: {
      type: Array,
      default: function () {
        return [];
      }
    },
    loadingTemplates: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeLocal: false,
      salvando: false,
      formasPagamentoLocais: [],
      form: {
        id: null,
        tipo: 'cobranca',
        nome: '',
        tipo_periodo: 'apos',
        dias_relativo_vencimento: 7,
        ids_formas_pagamento: [],
        id_forma_pagamento: null,
        template_nome: '',
        template_id: null,
        equipes: '',
        ativo: true
      }
    };
  },
  computed: {
    isEdit() {
      return !!(this.form && this.form.id);
    },
    listaFormasPagamento() {
      if (this.formasPagamento && this.formasPagamento.length > 0) {
        return this.formasPagamento;
      }
      return this.formasPagamentoLocais || [];
    }
  },
  watch: {
    active(val) {
      this.activeLocal = val;
      if (val) {
        this.popularForm();
        this.carregarFormasPagamento();
      }
    },
    activeLocal(val) {
      this.$emit('update:active', val);
    },
    'form.tipo_periodo'(val) {
      if (val === 'no_vencimento') {
        this.form.dias_relativo_vencimento = 0;
      }
    }
  },
  methods: {
    async carregarFormasPagamento() {
      if (this.formasPagamento && this.formasPagamento.length > 0) {
        this.formasPagamentoLocais = this.formasPagamento;
        return;
      }
      try {
        const res = await this.$http.get('formasPagamento');
        this.formasPagamentoLocais = res && res.data ? res.data : (Array.isArray(res) ? res : []);
      } catch (err) {
        console.warn("Erro ao buscar formas de pagamento no modal:", err);
      }
    },
    obterFormaObj(id) {
      if (!id) return null;
      const lista = this.listaFormasPagamento;
      if (!lista || lista.length === 0) {
        return { id: id, nome: 'Forma #' + id };
      }
      const encontrada = lista.find(f => Number(f.id) === Number(id));
      return encontrada || { id: id, nome: 'Forma #' + id };
    },
    obterFormaNome(id) {
      const forma = this.obterFormaObj(id);
      return forma ? forma.nome : 'Forma #' + id;
    },
    obterFormaIcon(id) {
      const forma = this.obterFormaObj(id);
      const nome = String((forma && forma.nome) || '').toUpperCase();
      const idNum = Number(id);
      if (idNum === 6 || nome.indexOf('PIX') !== -1) return 'pix';
      if (idNum === 2 || idNum === 3 || nome.indexOf('CART') !== -1 || nome.indexOf('CRED') !== -1 || nome.indexOf('DEB') !== -1) return 'card';
      if (idNum === 4 || nome.indexOf('DINHEIRO') !== -1 || nome.indexOf('ESPECIE') !== -1) return 'cash';
      if (idNum === 5 || nome.indexOf('TRANSF') !== -1 || nome.indexOf('TED') !== -1 || nome.indexOf('DOC') !== -1 || nome.indexOf('DEPOS') !== -1) return 'bank';
      if (idNum === 1 || nome.indexOf('BOLETO') !== -1) return 'barcode';
      return 'card';
    },
    obterBadgeClass(id) {
      const forma = this.obterFormaObj(id);
      const nome = String((forma && forma.nome) || '').toUpperCase();
      const idNum = Number(id);
      if (idNum === 6 || nome.indexOf('PIX') !== -1) return 'badge-apple-pix';
      if (idNum === 2 || idNum === 3 || nome.indexOf('CART') !== -1 || nome.indexOf('CRED') !== -1 || nome.indexOf('DEB') !== -1) return 'badge-apple-cartao';
      if (idNum === 4 || nome.indexOf('DINHEIRO') !== -1 || nome.indexOf('ESPECIE') !== -1) return 'badge-apple-dinheiro';
      if (idNum === 5 || nome.indexOf('TRANSF') !== -1 || nome.indexOf('TED') !== -1 || nome.indexOf('DOC') !== -1 || nome.indexOf('DEPOS') !== -1) return 'badge-apple-transferencia';
      if (idNum === 1 || nome.indexOf('BOLETO') !== -1) return 'badge-apple-boleto';
      return 'badge-apple-outro';
    },
    selecionarTodasFormas() {
      const lista = this.listaFormasPagamento;
      if (!lista || lista.length === 0) return;
      this.form.ids_formas_pagamento = lista.map(f => Number(f.id));
    },
    limparFormas() {
      this.form.ids_formas_pagamento = [];
    },
    removerForma(id) {
      const idx = this.form.ids_formas_pagamento.indexOf(Number(id));
      if (idx !== -1) {
        this.form.ids_formas_pagamento.splice(idx, 1);
      }
    },
    popularForm() {
      if (this.regra && this.regra.id) {
        let idsFormas = [];
        if (this.regra.ids_formas_pagamento) {
          idsFormas = String(this.regra.ids_formas_pagamento)
            .split(',')
            .map(n => Number(n.trim()))
            .filter(n => !isNaN(n) && n > 0);
        } else if (this.regra.id_forma_pagamento) {
          idsFormas = [Number(this.regra.id_forma_pagamento)];
        }

        this.form = {
          id: this.regra.id,
          tipo: 'cobranca',
          nome: this.regra.nome || '',
          tipo_periodo: this.regra.tipo_periodo || 'apos',
          dias_relativo_vencimento: this.regra.dias_relativo_vencimento !== null && this.regra.dias_relativo_vencimento !== undefined ? Number(this.regra.dias_relativo_vencimento) : 0,
          ids_formas_pagamento: idsFormas,
          id_forma_pagamento: idsFormas.length > 0 ? idsFormas[0] : null,
          template_nome: this.regra.template_nome || '',
          template_id: this.regra.template_id || null,
          equipes: this.regra.equipes || '',
          ativo: this.regra.ativo !== undefined ? Boolean(this.regra.ativo) : true
        };
      } else {
        this.form = {
          id: null,
          tipo: 'cobranca',
          nome: '',
          tipo_periodo: 'apos',
          dias_relativo_vencimento: 7,
          ids_formas_pagamento: [],
          id_forma_pagamento: null,
          template_nome: '',
          template_id: null,
          equipes: '',
          ativo: true
        };
      }
    },
    fechar() {
      this.activeLocal = false;
      this.$emit('update:active', false);
    },
    validar() {
      if (!this.form.nome || !this.form.nome.trim()) {
        this.$vs.notify({
          title: "Campo Obrigatório",
          text: "Informe o nome ou identificação da regra.",
          color: "warning"
        });
        return false;
      }
      if (this.form.tipo_periodo !== 'no_vencimento') {
        const dias = Number(this.form.dias_relativo_vencimento);
        if (isNaN(dias) || dias <= 0) {
          this.$vs.notify({
            title: "Quantidade de Dias",
            text: "Informe a quantidade de dias antes ou após o vencimento (mínimo 1).",
            color: "warning"
          });
          return false;
        }
      }
      if (!this.form.template_nome) {
        this.$vs.notify({
          title: "Template Obrigatório",
          text: "Selecione o template do WATI que será disparado.",
          color: "warning"
        });
        return false;
      }
      return true;
    },
    async salvar() {
      if (!this.validar()) return;

      this.salvando = true;
      try {
        const payload = {
          ...this.form,
          ids_formas_pagamento: this.form.ids_formas_pagamento && this.form.ids_formas_pagamento.length > 0
            ? this.form.ids_formas_pagamento.join(',')
            : null,
          id_forma_pagamento: this.form.ids_formas_pagamento && this.form.ids_formas_pagamento.length > 0
            ? this.form.ids_formas_pagamento[0]
            : null
        };

        let res;
        if (this.isEdit) {
          res = await this.$http.put(`regua-disparos/${this.form.id}`, payload);
        } else {
          res = await this.$http.post('regua-disparos', payload);
        }

        this.$vs.notify({
          title: "Sucesso!",
          text: this.isEdit ? "Regra de cobrança atualizada com sucesso." : "Regra de cobrança criada com sucesso.",
          color: "success"
        });

        this.$emit('salvo', res && res.data ? res.data : res);
        this.fechar();
      } catch (err) {
        const error = this.$httpErrors ? this.$httpErrors(err) : { text: err.message };
        this.$vs.notify({
          title: "Erro ao salvar regra",
          text: error.text || err.message,
          color: "danger"
        });
      } finally {
        this.salvando = false;
      }
    }
  }
};
</script>

<style scoped>
/* =========================================================
   Modal Dialog Styling (Padrão Apple / SCORPIO ERP)
========================================================= */
.modal-regra-cobranca >>> .vs-popup {
  max-width: 680px;
  width: 95%;
  border-radius: 16px;
}

.apple-modal-body {
  padding: 8px 12px;
}

.apple-callout {
  display: flex;
  align-items: flex-start;
  background: #f0f7ff;
  border: 1px solid #cce4ff;
  border-radius: 10px;
  padding: 10px 14px;
}

.apple-callout-text {
  font-size: 12.5px;
  color: #1e40af;
  line-height: 1.45;
  margin: 0;
}

.apple-form-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 14px 16px;
}

.apple-form-group {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.apple-form-header-row {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
  margin-bottom: 6px !important;
}

.apple-form-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.apple-link-action {
  background: none;
  border: none;
  color: #0284c7;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: color 0.15s ease;
}

.apple-link-action:hover {
  text-decoration: underline;
  color: #0369a1;
}

.apple-link-action.action-danger {
  color: #64748b;
}

.apple-link-action.action-danger:hover {
  color: #e11d48;
}

.action-separator {
  color: #cbd5e1;
  font-size: 12px;
}

.apple-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 5px;
}

.apple-helper-text {
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}

.apple-native-input {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  padding: 0 12px;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  transition: all 0.18s ease;
}

.apple-native-input:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
}

.apple-native-input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

/* =========================================================
   Badges / Chips das Formas de Pagamento Selecionadas
========================================================= */
.forma-chip-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;
}

.forma-chip-name {
  margin-right: 6px;
  white-space: nowrap;
}

.forma-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.08);
  color: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
  margin-left: 2px;
  outline: none;
}

.forma-chip-remove:hover {
  background: #ef4444;
  color: #ffffff;
  transform: scale(1.15);
}

.badge-apple-pix {
  background: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  color: #15803d !important;
}

.badge-apple-boleto {
  background: #f0f9ff !important;
  border: 1px solid #bae6fd !important;
  color: #0369a1 !important;
}

.badge-apple-cartao {
  background: #faf5ff !important;
  border: 1px solid #e9d5ff !important;
  color: #7e22ce !important;
}

.badge-apple-dinheiro {
  background: #ecfdf5 !important;
  border: 1px solid #a7f3d0 !important;
  color: #047857 !important;
}

.badge-apple-transferencia {
  background: #eef2ff !important;
  border: 1px solid #c7d2fe !important;
  color: #4338ca !important;
}

.badge-apple-outro {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  color: #475569 !important;
}

.apple-pill-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 100px;
}

.badge-neutral {
  background: #f1f5f9;
  color: #475569;
}

.apple-switch-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 4px;
}

.apple-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.apple-dialog-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  border-radius: 9px;
  padding: 0 20px;
  height: 38px;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  border: none;
  outline: none;
}

.apple-btn-save {
  background: linear-gradient(180deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(2, 132, 199, 0.25);
}

.apple-btn-save:hover {
  background: linear-gradient(180deg, #0369a1 0%, #075985 100%);
  box-shadow: 0 4px 10px rgba(2, 132, 199, 0.32);
  transform: translateY(-1px);
}

.apple-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.apple-btn-cancel {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.apple-btn-cancel:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #94a3b8;
}
</style>
